import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import defaultConfig from '../config/confirmation-modal-config.json';
import { ConfirmationModalProps } from '../types';

/**
 * ConfirmationModal Component
 *
 * Displays a confirmation dialog for validating answer selections.
 * Shows the selected answer and provides clear confirm/cancel actions.
 *
 * Features:
 * - Answer preview in styled box
 * - Dual action buttons (validate/change)
 * - Customizable text via config
 * - Keyboard accessible (Escape to close, Tab navigation)
 * - Responsive layout (mobile & desktop)
 * - Design System compliant
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   isOpen={showModal}
 *   selectedAnswerText="Answer A"
 *   onClose={() => setShowModal(false)}
 *   onConfirm={handleValidation}
 * />
 * ```
 */
export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedAnswerText,
  title = defaultConfig.defaults.title,
  description = defaultConfig.defaults.description,
  confirmButtonText = defaultConfig.defaults.confirmButtonText,
  cancelButtonText = defaultConfig.defaults.cancelButtonText,
  showAnswerPreview = defaultConfig.defaults.showAnswerPreview,
  allowDismiss = defaultConfig.defaults.allowDismiss,
}: ConfirmationModalProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open && allowDismiss) {
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className={defaultConfig.styles.maxWidth}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-xl">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-base">{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {showAnswerPreview && (
          <div
            className={`my-4 rounded-lg p-4 ${defaultConfig.styles.answerPreviewBackground} ${defaultConfig.styles.answerPreviewBorder}`}
          >
            <p className="font-body font-semibold text-livelinx-text">{selectedAnswerText || ''}</p>
          </div>
        )}

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel onClick={onClose} className="font-body">
            {cancelButtonText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-primary font-body hover:bg-primary/90"
          >
            {confirmButtonText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
